import { useState } from "react";
import { Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useTranslation } from "react-i18next";

interface StatusFilterProps {
  statusFilter: string[];
  setStatusFilter: (status: string[]) => void;
}

export const StatusFilter = ({ 
  statusFilter, 
  setStatusFilter 
}: StatusFilterProps) => {
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const { t } = useTranslation();
  
  // Define fixed status options with lowercase values as used in the filter logic
  const statusOptions = ["new", "looking", "open", "unavailable", "hired"];

  // Toggle status selection
  const toggleStatus = (status: string) => {
    if (statusFilter.includes(status)) {
      setStatusFilter(statusFilter.filter(s => s !== status));
    } else {
      setStatusFilter([...statusFilter, status]);
    }
  };

  return (
    <DropdownMenu open={statusMenuOpen} onOpenChange={setStatusMenuOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          className="flex items-center gap-2"
          aria-expanded={statusMenuOpen}
          data-state={statusMenuOpen ? "open" : "closed"}
        >
          <Filter className="h-4 w-4" />
          <span>{t("candidates.filters.statusButton")}</span>
          {statusFilter.length > 0 && (
            <span className="ml-1 bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-300 text-xs font-medium px-2 py-0.5 rounded-sm">
              {statusFilter.length}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        className="w-56 p-3 bg-white dark:bg-gray-900"
        align="start"
        onInteractOutside={() => setStatusMenuOpen(false)}
      >
        <div className="space-y-2 max-h-60 overflow-y-auto">
          {statusOptions.map(status => (
            <div key={status} className="flex items-center space-x-2">
              <Checkbox 
                id={`status-${status}`} 
                checked={statusFilter.includes(status)}
                onCheckedChange={() => toggleStatus(status)}
              />
              <Label 
                htmlFor={`status-${status}`}
                className="text-sm font-normal cursor-pointer flex-1"
              >
                {t(`candidates.filters.status.${status}`)}
              </Label>
            </div>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
